'use client'

import React, { useState, useEffect, useRef, createContext, useContext, ReactNode, FC } from 'react'
import { onAuthStateChanged, User } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { updateLastAccess } from '@/lib/actions/updateLastAccess'
import { resolveAuthAccessEvent } from '@/lib/utils/authAccessEvent'
import { getDeviceType } from '@/lib/utils/login'
import { isStandalone } from '@/lib/utils/pwa'
import { LastAccessDevice } from '@/lib/interfaces/bar'

interface AuthContextProps {
  user: User | null;
}

const AuthContext = createContext<AuthContextProps>({ user: null })

export const useAuthContext = (): AuthContextProps => useContext(AuthContext)

interface AuthContextProviderProps {
  children: ReactNode;
}

const TRANSIENT_NULL_GRACE_MS = 1500

export const AuthContextProvider: FC<AuthContextProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const lastUserRef = useRef<User | null>(null)

  useEffect(() => {
    let nullTimer: ReturnType<typeof setTimeout> | null = null

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (nullTimer !== null) {
        clearTimeout(nullTimer)
        nullTimer = null
      }

      if (firebaseUser) {
        lastUserRef.current = firebaseUser
        setUser(firebaseUser)
        const base = getDeviceType()
        const device: LastAccessDevice = isStandalone() ? (base === 'iphone' ? 'pwa-ios' : base === 'android' ? 'pwa-android' : 'pwa-pc') : (base as LastAccessDevice)
        updateLastAccess(firebaseUser.uid, device, resolveAuthAccessEvent(firebaseUser.uid, firebaseUser.metadata.lastSignInTime)).catch(() => {})
      } else {
        // Firebase ya resolvió el estado inicial. Si había un usuario en esta
        // pestaña, damos margen a una actualización temporal entre pestañas;
        // un signOut real sin recuperación acabará propagando null.
        if (lastUserRef.current) {
          nullTimer = setTimeout(() => {
            lastUserRef.current = null
            setUser(null)
            nullTimer = null
          }, TRANSIENT_NULL_GRACE_MS)
        } else {
          setUser(null)
        }
      }
      setLoading(false)
    })

    return () => {
      if (nullTimer !== null) clearTimeout(nullTimer)
      unsubscribe()
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user }}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
